import styled from 'styled-components';

import { DELIVERY_PLURAL_STATES_LABELS } from './constants/states';

import { DeliveryState, GetDeliveriesQuery } from 'data/__generated__';

import { DeliveryTrelloItem } from './components/DeliveryTrelloItem';
import { TrelloView } from 'components/TrelloView/TrelloView';

type DeliveriesTrelloViewProps = {
    deliveries: GetDeliveriesQuery['deliveries'];
    selectedDeliveryIds: string[];
    setSelectedDeliveryIds: React.Dispatch<React.SetStateAction<string[]>>;
    toggleDeliverySelection: (deliveryId: string) => void;
};

const STATE_COLUMNS_TO_DISPLAY: DeliveryState[] = [
    DeliveryState.Created,
    DeliveryState.Packaging,
    DeliveryState.Ready,
    DeliveryState.InProgress,
    DeliveryState.Delivered,
    DeliveryState.NotDelivered,
    DeliveryState.Aborted,
    DeliveryState.Archived,
];

const REVERSED_STATES: DeliveryState[] = [
    DeliveryState.Aborted,
    DeliveryState.Archived,
    DeliveryState.Delivered,
    DeliveryState.NotDelivered,
];

const DELIVERY_STATE_COLORS: Partial<Record<DeliveryState, string>> = {
    [DeliveryState.Created]: '#94a3b8', // slate
    [DeliveryState.Packaging]: '#3b82f6', // blue
    [DeliveryState.Ready]: '#f59e0b', // amber
    [DeliveryState.InProgress]: '#6366f1', // indigo
    [DeliveryState.Delivered]: '#22c55e', // green
    [DeliveryState.NotDelivered]: '#f97316', // orange
    [DeliveryState.Aborted]: '#ef4444', // red
    [DeliveryState.Archived]: '#a1a1aa', // zinc
};

function sortByDeliveryDate(
    deliveryA: GetDeliveriesQuery['deliveries'][number],
    deliveryB: GetDeliveriesQuery['deliveries'][number],
) {
    if ((deliveryA.deliveryDate && !deliveryB.deliveryDate) || deliveryA.deliveryDate > deliveryB.deliveryDate) {
        return 1;
    }
    if (deliveryA.deliveryDate === deliveryB.deliveryDate) {
        return deliveryA.updatedAt > deliveryB.updatedAt ? 1 : -1;
    }
    return -1;
}

export const DeliveriesTrelloView = ({
    deliveries,
    selectedDeliveryIds,
    setSelectedDeliveryIds,
    toggleDeliverySelection,
}: DeliveriesTrelloViewProps) => {
    function getDeliveriesByState({ state }: { state: DeliveryState }) {
        const sortedDeliveries = deliveries.filter((delivery) => delivery.state === state).sort(sortByDeliveryDate);
        if (REVERSED_STATES.includes(state)) {
            sortedDeliveries.reverse();
        }
        return sortedDeliveries.map((delivery) => ({
            _id: delivery._id,
            content: (
                <DeliveryTrelloItem
                    delivery={delivery}
                    isSelected={selectedDeliveryIds.includes(delivery._id)}
                    toggleDeliverySelection={toggleDeliverySelection}
                />
            ),
        }));
    }

    const initialData = {
        columns: STATE_COLUMNS_TO_DISPLAY.map((state) => ({
            title: DELIVERY_PLURAL_STATES_LABELS[state],
            accentColor: DELIVERY_STATE_COLORS[state],
            items: getDeliveriesByState({ state }),
        })),
    };

    return (
        <TrelloViewContainer>
            <TrelloView
                initialData={initialData}
                selectedItemIds={selectedDeliveryIds}
                setSelectedItemIds={setSelectedDeliveryIds}
                padding="0"
            />
        </TrelloViewContainer>
    );
};

const TrelloViewContainer = styled.div`
    flex: 1;
    min-width: 0;
    overflow-x: scroll;
`;
